import { filter, distinct, take, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Router, RoutesRecognized } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ApplicationRoutes,
  GenericRoutes,
  SettingsRoutes,
  SOURCE_ID,
  TEMPLATE_ID,
  getRouteUrlByAccountGroupId,
} from '../common/routes';

@Injectable({ providedIn: 'root' })
export class CustomerVoiceRouterService {
  private readonly accountGroupIdSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  public constructor(private readonly router: Router) {
    this.router.events
      .pipe(filter((url: any) => url instanceof RoutesRecognized))
      .subscribe((url: RoutesRecognized) => {
        this.accountGroupIdSubject.next(url.urlAfterRedirects.split('/')[3]);
      });
  }

  public getRouteUrl(route: string): Observable<string> {
    return this.accountGroupId.pipe(
      map((accountGroupId: string) => getRouteUrlByAccountGroupId(route, accountGroupId)),
    );
  }

  public getSettingsRouteUrl(settingsPage: string): Observable<string> {
    return this.getRouteUrl(`${ApplicationRoutes.settings}/${settingsPage}`);
  }

  public navigateByUrl(route: string): void {
    this.getRouteUrl(route)
      .pipe(take(1))
      .subscribe((url) => this.router.navigateByUrl(url));
  }

  public navigateToSettings(settingsPage: string): void {
    this.navigateByUrl(`${ApplicationRoutes.settings}/${settingsPage}`);
  }

  public getShortenedTemplateIdForPath(templateId: string): string {
    return templateId.split('/')[6];
  }

  public navigateToTemplate(templateId: string): void {
    if (templateId) {
      this.navigateToSettings(
        SettingsRoutes.editTemplate.replace(`:templateId`, this.getShortenedTemplateIdForPath(templateId)),
      );
    } else {
      this.navigateToSettings(SettingsRoutes.editTemplate.replace(`:templateId`, ''));
    }
  }

  public navigateToEditEmailTemplate(templateId: string): void {
    this.navigateByUrl(ApplicationRoutes.editEmailTemplate.replace(`:${TEMPLATE_ID}`, templateId));
  }

  public navigateToEditSmsTemplate(templateId: string): void {
    this.navigateByUrl(ApplicationRoutes.editSmsTemplate.replace(`:${TEMPLATE_ID}`, templateId));
  }

  public get accountGroupId(): Observable<string> {
    return this.accountGroupIdSubject.asObservable().pipe(
      filter((accountGroupId) => !!accountGroupId && accountGroupId.startsWith('AG-')),
      distinct(),
    );
  }

  public getReviewShareUrl(sourceId: number): Observable<string> {
    return this.accountGroupId.pipe(
      map((accountGroupId) =>
        getRouteUrlByAccountGroupId(GenericRoutes.reviewShareUrl, accountGroupId).replace(
          `:${SOURCE_ID}`,
          sourceId.toString(),
        ),
      ),
    );
  }
}
